/**
 * @file WsSubscription - WebSocket client with auto-reconnect.
 *
 * Manages a WebSocket connection with:
 * - Automatic reconnection with exponential backoff
 * - Server-push event handling
 * - Correlated request-response via message `id` field
 * - Resume support on reconnect
 *
 * Uses the browser-native WebSocket API.
 */

import { CoreError } from '../shared/index.ts'
import { NetworkError } from './errors.ts'
import type {
  BinaryCallback,
  CloseCallback,
  DataCallback,
  ErrorCallback,
  StatusCallback,
  Subscription,
  SubscriptionStatus,
  Unsubscribe,
} from './types.ts'

////////////////////////////////////////////////////////////////////////////////
// Types
////////////////////////////////////////////////////////////////////////////////

/**
 * WebSocket message envelope.
 *
 * All text frames sent and received over the WebSocket use this shape.
 * Server-initiated pushes omit `id`; request-response pairs include `id`
 * for correlation — the response is matched purely by `id`, and the only
 * reserved response `type` is `'error'`, which rejects the pending
 * promise with `payload.message` as the error message. Any other
 * response `type` resolves the promise with its `payload`.
 *
 * Binary frames bypass this envelope — see {@link WsSubscription.sendBinary}
 * / {@link WsSubscription.onBinary}.
 */
export type WsMessage = {
  id?: string
  type: string
  payload?: unknown
}

/**
 * Structural surface of the WebSocket implementations this client can
 * drive — the browser-native `WebSocket` and undici's `WebSocket` both
 * satisfy it. Declared structurally (rather than `lib.dom`'s
 * `WebSocket`) so a Node-side caller can inject an undici socket
 * constructed with an mTLS `dispatcher` without this file naming
 * undici at all.
 *
 * @property binaryType - Set to `'arraybuffer'` on connect so binary
 *   frames arrive as `ArrayBuffer`, never `Blob`.
 * @property readyState - Standard readyState (1 = OPEN).
 * @property onopen - Open handler slot.
 * @property onclose - Close handler slot (receives `{code, reason}`).
 * @property onerror - Error handler slot.
 * @property onmessage - Message handler slot (receives `{data}`).
 * @property send - Send a text or binary frame.
 * @property close - Close the socket.
 */
export type WebSocketLike = {
  binaryType: string
  readyState: number
  onopen: (() => void) | null
  onclose: ((event: { code: number; reason: string }) => void) | null
  onerror: ((event: unknown) => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
  send(data: string | Uint8Array): void
  close(code?: number, reason?: string): void
}

/**
 * Factory producing the WebSocket for a subscription URL. The default
 * uses the runtime's global `WebSocket`; Node-side callers that need
 * an mTLS client cert inject a factory that constructs undici's
 * `WebSocket` with a TLS-configured `dispatcher` (see
 * `WsClientTransport`'s `tls` prop).
 */
export type CreateWebSocket = (url: string) => WebSocketLike

/** Standard WebSocket OPEN readyState. */
const WS_OPEN = 1

/**
 * Configuration for {@link WsSubscription}.
 *
 * @property url - WebSocket endpoint (e.g., `'ws://localhost:3000/api/updates'`)
 * @property initialReconnectDelay - First reconnect delay in ms (default: 1000)
 * @property maxReconnectDelay - Backoff ceiling in ms (default: 30000)
 * @property requestTimeout - How long `request()` waits
 *   before rejecting (default: 10000)
 * @property createWebSocket - Optional socket factory (default: the
 *   runtime's global `WebSocket`). See {@link CreateWebSocket}.
 */
export type WsSubscriptionProps = {
  url: string
  initialReconnectDelay?: number
  maxReconnectDelay?: number
  requestTimeout?: number
  createWebSocket?: CreateWebSocket
}

/**
 * Pending request awaiting a correlated response.
 *
 * Created by {@link WsSubscription.request} and resolved or rejected
 * when a message with the matching `id` arrives or the timer expires.
 */
type PendingRequest = {
  resolve: (payload: unknown) => void
  reject: (error: Error) => void
  timer: ReturnType<typeof setTimeout>
}

////////////////////////////////////////////////////////////////////////////////
// Helpers
////////////////////////////////////////////////////////////////////////////////

/**
 * Push a handler onto a list and return a closure that removes it.
 * Shared by all `onX` registration methods to avoid duplicating the
 * splice logic.
 */
function register<T>(list: T[], handler: T): Unsubscribe {
  list.push(handler)
  return () => {
    const i = list.indexOf(handler)
    if (i !== -1) list.splice(i, 1)
  }
}

////////////////////////////////////////////////////////////////////////////////
// WsSubscription
////////////////////////////////////////////////////////////////////////////////

/**
 * WebSocket client with auto-reconnect and
 * request-response correlation.
 *
 * Implements {@link Subscription} for server-push events and adds
 * bidirectional messaging via {@link send} (fire-and-forget) and
 * {@link request} (correlated request-response). On disconnect the
 * client reconnects with exponential backoff (1 s → 30 s) and sends
 * a `resume` frame so the server can send the current state.
 *
 * Uses the browser-native `WebSocket` API.
 *
 * @example
 * ```typescript
 * const ws = new WsSubscription({
 *   url: 'ws://localhost:3000/api/updates',
 * })
 *
 * // Listen for server pushes
 * ws.onData(msg => console.log(msg))
 *
 * // Correlated request-response
 * const briefing = await ws.request('briefing.get')
 *
 * // Fire-and-forget action
 * ws.send({ type: 'action.approve', payload: { id } })
 *
 * // Tear down
 * ws.unsubscribe()
 * ```
 */
export class WsSubscription implements Subscription<WsMessage> {
  //////////////////////////////////////////////////////////////////////////////
  // Instance Properties
  //////////////////////////////////////////////////////////////////////////////

  private readonly url: string
  private readonly initialReconnectDelay: number
  private readonly maxReconnectDelay: number
  private readonly requestTimeout: number
  private readonly createWebSocket: CreateWebSocket

  private ws: WebSocketLike | null = null
  private reconnectDelay: number
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private closed = false
  private reconnected = false

  private _status: SubscriptionStatus = 'connecting'
  private _error: CoreError | undefined = undefined

  private dataHandlers: DataCallback<WsMessage>[] = []
  private errorHandlers: ErrorCallback[] = []
  private statusHandlers: StatusCallback[] = []
  private binaryHandlers: BinaryCallback[] = []
  private closeHandlers: CloseCallback[] = []
  private pendingRequests = new Map<string, PendingRequest>()

  //////////////////////////////////////////////////////////////////////////////
  // Constructor
  //////////////////////////////////////////////////////////////////////////////

  constructor(props: WsSubscriptionProps) {
    this.url = props.url
    this.initialReconnectDelay = props.initialReconnectDelay ?? 1000
    this.maxReconnectDelay = props.maxReconnectDelay ?? 30000
    this.requestTimeout = props.requestTimeout ?? 10000
    this.createWebSocket =
      props.createWebSocket ??
      (url => new WebSocket(url) as unknown as WebSocketLike)
    this.reconnectDelay = this.initialReconnectDelay

    this.connect()
  }

  //////////////////////////////////////////////////////////////////////////////
  // Subscription Interface
  //////////////////////////////////////////////////////////////////////////////

  /** Current connection status. */
  get status(): SubscriptionStatus {
    return this._status
  }

  /** Most recent connection error, if any. */
  get error(): CoreError | undefined {
    return this._error
  }

  /**
   * Register a status-change handler. The handler is called
   * immediately with the current status, then on every transition.
   *
   * @returns Unsubscribe function that detaches this handler.
   */
  onStatus(handler: StatusCallback): Unsubscribe {
    const unsub = register(this.statusHandlers, handler)
    handler(this._status)
    return unsub
  }

  /**
   * Register a handler for server-push messages.
   *
   * @returns Unsubscribe function that detaches this handler.
   */
  onData(handler: DataCallback<WsMessage>): Unsubscribe {
    return register(this.dataHandlers, handler)
  }

  /**
   * Register a handler for connection errors.
   *
   * @returns Unsubscribe function that detaches this handler.
   */
  onError(handler: ErrorCallback): Unsubscribe {
    return register(this.errorHandlers, handler)
  }

  /**
   * Register a handler for incoming binary frames. The frame is
   * delivered as a `Uint8Array` view over a freshly-allocated buffer;
   * callers can retain it safely.
   *
   * @returns Unsubscribe function that detaches this handler.
   */
  onBinary(handler: BinaryCallback): Unsubscribe {
    return register(this.binaryHandlers, handler)
  }

  /**
   * Whether a WS close code is an app-level *fatal* close that must not
   * trigger a reconnect. Two bands carry them: the 4000–4099 protocol
   * band, and the 4400–4499 band that mirrors HTTP 4xx client-errors
   * (e.g. 4403 over-budget, 4429 at-capacity). Reconnecting on either
   * just loops into the same close — a 4403 reconnect is the
   * budget-exhausted flicker loop (#1315) — so both are terminal.
   *
   * @param code - The WebSocket close code.
   * @returns `true` when the code is a non-reconnectable fatal close.
   */
  static isFatalCloseCode(code: number): boolean {
    return (code >= 4000 && code <= 4099) || (code >= 4400 && code <= 4499)
  }

  /**
   * Register a handler for socket close. Fires on every close,
   * including explicit {@link unsubscribe} calls (code 1000). Close
   * codes {@link isFatalCloseCode | classified as fatal} (the 4000–4099
   * protocol band + the 4400–4499 HTTP-4xx-mirroring band) suppress
   * reconnect; other codes continue to backoff-reconnect unless the
   * subscription is already closed.
   *
   * @returns Unsubscribe function that detaches this handler.
   */
  onClose(handler: CloseCallback): Unsubscribe {
    return register(this.closeHandlers, handler)
  }

  /**
   * Close the WebSocket and stop reconnecting.
   *
   * Fires registered {@link onClose} handlers with code 1000 before
   * tearing down, then rejects any pending {@link request} calls with
   * a "Subscription closed" error. After calling this method the
   * instance cannot be reused.
   */
  unsubscribe(): void {
    if (this.closed) return
    this.closed = true
    this.emitClose(1000, 'unsubscribed')
    this.cleanup()

    for (const [, pending] of this.pendingRequests) {
      clearTimeout(pending.timer)
      pending.reject(new Error('Subscription closed'))
    }
    this.pendingRequests.clear()
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public Methods
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Send a fire-and-forget message. Silently drops the message
   * if the socket is not currently open.
   */
  send(message: WsMessage): void {
    if (this.ws && this.ws.readyState === WS_OPEN) {
      this.ws.send(JSON.stringify(message))
    }
  }

  /**
   * Send a fire-and-forget binary frame. Silently drops the frame
   * if the socket is not currently open. Used for protocols like the
   * gateway terminal pty bridge where the wire carries raw bytes
   * alongside the JSON control envelope.
   */
  sendBinary(data: Uint8Array): void {
    if (this.ws && this.ws.readyState === WS_OPEN) {
      this.ws.send(data)
    }
  }

  /**
   * Send a message and wait for a correlated response.
   *
   * Generates a unique `id`, sends `{ id, type, payload }`, and
   * resolves when a response carrying the same `id` arrives.
   * Rejects if no response arrives within `requestTimeout` ms
   * or if the response `type` is `'error'`.
   *
   * @param type - Message type (e.g., `'briefing.get'`)
   * @param payload - Optional request payload
   * @returns The response `payload`, typed as `R`
   */
  request<R = unknown>(type: string, payload?: unknown): Promise<R> {
    return new Promise<R>((resolve, reject) => {
      const id = crypto.randomUUID()

      const timer = setTimeout(() => {
        this.pendingRequests.delete(id)
        reject(new Error(`Request timeout: ${type}`))
      }, this.requestTimeout)

      this.pendingRequests.set(id, {
        resolve: resolve as (payload: unknown) => void,
        reject,
        timer,
      })

      this.send({ id, type, payload })
    })
  }

  //////////////////////////////////////////////////////////////////////////////
  // Private Methods
  //////////////////////////////////////////////////////////////////////////////

  /** Transition status and notify handlers. */
  private setStatus(status: SubscriptionStatus): void {
    if (this._status === status) return
    this._status = status
    if (status === 'connected') {
      this._error = undefined
    }
    for (const handler of this.statusHandlers) {
      try {
        handler(status)
      } catch {
        // Ignore handler errors
      }
    }
  }

  /** Broadcast a message to all data handlers. */
  private emitData(data: WsMessage): void {
    for (const handler of this.dataHandlers) {
      try {
        handler(data)
      } catch {
        // Ignore handler errors
      }
    }
  }

  /** Broadcast a binary frame to all binary handlers. */
  private emitBinary(data: Uint8Array): void {
    for (const handler of this.binaryHandlers) {
      try {
        handler(data)
      } catch {
        // Ignore handler errors
      }
    }
  }

  /** Notify close handlers with the observed code/reason. */
  private emitClose(code: number, reason: string): void {
    for (const handler of this.closeHandlers) {
      try {
        handler(code, reason)
      } catch {
        // Ignore handler errors
      }
    }
  }

  /** Store the error and notify error handlers. */
  private emitError(error: CoreError): void {
    this._error = error
    for (const handler of this.errorHandlers) {
      try {
        handler(error)
      } catch {
        // Ignore handler errors
      }
    }
  }

  /** Open a new WebSocket and wire event handlers. */
  private connect(): void {
    if (this.closed) return

    this.setStatus('connecting')

    const ws = this.createWebSocket(this.url)
    this.ws = ws
    ws.binaryType = 'arraybuffer'

    ws.onopen = () => {
      this.setStatus('connected')
      this.reconnectDelay = this.initialReconnectDelay

      // Send resume on reconnect so the server sends current state
      if (this.reconnected) {
        this.send({ type: 'resume' })
      }
      this.reconnected = true
    }

    ws.onclose = event => {
      this.emitClose(event.code, event.reason)
      // App-level fatal closes are not reconnect-eligible — the condition
      // won't heal by retrying, so a reconnect just loops into the same
      // close. Two bands carry them: the 4000–4099 protocol band (invalid
      // token, session gone, already attached, backend attach failure) and
      // the 4400–4499 band that mirrors HTTP 4xx client-errors (e.g. 4403
      // over-budget, 4429 at-capacity). A 4403 reconnecting is exactly the
      // budget-exhausted flicker loop this guard prevents (#1315).
      if (WsSubscription.isFatalCloseCode(event.code)) {
        this.closed = true
        this.setStatus('disconnected')
        return
      }
      this.setStatus('disconnected')
      this.scheduleReconnect()
    }

    ws.onerror = () => {
      this.emitError(CoreError.from(new NetworkError(), 'WS_ERROR'))
    }

    ws.onmessage = event => {
      if (typeof event.data === 'string') {
        this.handleMessage(event.data)
      } else if (event.data instanceof ArrayBuffer) {
        this.emitBinary(new Uint8Array(event.data))
      }
      // Ignore other shapes (e.g. Blob) — with binaryType='arraybuffer'
      // we only ever see string or ArrayBuffer.
    }
  }

  /**
   * Parse an incoming frame. Correlated responses are routed to
   * their pending request; all other messages are emitted as data.
   */
  private handleMessage(raw: string): void {
    try {
      const message = JSON.parse(raw) as WsMessage
      if (!message.type) return

      // Check if this is a response to a pending request
      if (message.id && this.pendingRequests.has(message.id)) {
        const pending = this.pendingRequests.get(message.id)!
        this.pendingRequests.delete(message.id)
        clearTimeout(pending.timer)

        if (message.type === 'error') {
          const payload = message.payload as { message?: string } | undefined
          pending.reject(new Error(payload?.message ?? 'Request failed'))
        } else {
          pending.resolve(message.payload)
        }
        return
      }

      // Server push — emit to data handlers
      this.emitData(message)
    } catch {
      // Ignore malformed messages
    }
  }

  /** Detach listeners, close the socket, and cancel timers. */
  private cleanup(): void {
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.close()
      this.ws = null
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  /** Queue a reconnect after the current backoff delay. */
  private scheduleReconnect(): void {
    if (this.closed) return

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, this.reconnectDelay)

    this.reconnectDelay = Math.min(
      this.reconnectDelay * 2,
      this.maxReconnectDelay,
    )
  }
}
